import {
  faArchive,
  faArrowRotateLeft,
  faArrowsLeftRight,
  faBackward,
  faCheckCircle,
  faCircleExclamation,
  faClock,
  faCopy,
  faDownload,
  faEllipsisV,
  faPaperPlane,
  faPencil,
  faPlus,
  faRefresh,
  faTrash,
  type IconDefinition,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useKeycloak } from "@react-keycloak/web";
import { DataTable } from "@ventumsoftware/react-toolkit";
import React, { type FC, useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router";
import { useApi, useFetchedResource } from "../api/APIContext";
import { useApiCall } from "../api/api";
import type { Assessor, Company, Exam, ExamParticipant, Project } from "../api/dto";
import type { QuestionSet } from "../api/dto/QuestionSet";
import { Card } from "../components/Card";
import { ContentContainer } from "../components/content/ContentContainer";
import { ContextMenu } from "../components/content/ContextMenu";
import { PageHeader } from "../components/content/PageHeader";
import { SectionDivider } from "../components/content/SectionDivider";
import { SectionHeader } from "../components/content/SectionHeader";
import { Badge, BadgeStyle } from "../components/data/Badge";
import { ScoreBadge } from "../components/data/ScoreBadge";
import { TablePlaceholder } from "../components/data/TablePlaceholder";
import { Button, IconButton } from "../components/form/Button";
import { Select } from "../components/form/Select";
import { Loading } from "../components/Loading";
import { useModal } from "../components/layout/ModalProvider";
import { useRefresh, useRefreshEffect } from "../components/RefreshController";
import config from "../config";
import { AddParticipantModal } from "../modals/AddParticipantModal";
import { ArchiveExamModal } from "../modals/ArchiveExamModal";
import { ArchiveParticipantModal } from "../modals/ArchiveParticipantModal";
import { EditParticipantModal } from "../modals/EditParticipantModal";
import { CloneExamModal } from "../modals/CloneExamModal";
import { EditExamModal } from "../modals/EditExamModal";
import { MoveExamParticipantModal } from "../modals/MoveExamParticipantModal";
import { PermanentDeleteExamModal } from "../modals/PermanentDeleteExamModal";
import { PermanentDeleteParticipantModal } from "../modals/PermanentDeleteParticipantModal";
import { RegenResultsModal } from "../modals/RegenResultsModal";
import { RevertExamModal } from "../modals/RevertExamModal";
import { SendExamReportModal } from "../modals/SendExamReportModal";
import { resolveScoringMap } from "../util/categoryScoring";

interface EnrichedExam extends Exam {
  projectName: string;
  companyName: string;
  questionSetName: string;
  project: Project;
  company: Company;
  questionSet: QuestionSet;
  participants: ExamParticipant[];
}
export const ExamDetailsPage: FC<{}> = () => {
  const { examId } = useParams();
  const { exams, projects, companies, questionSets, assessors, loading } = useApi();
  // check if customer
  const { keycloak } = useKeycloak();
  const roles = useMemo(() => keycloak.tokenParsed?.realm_access?.roles ?? [], [keycloak.tokenParsed]);
  const isCustomer = roles.includes("customer");
  // Participants of an exam that hasn't started yet (concept) can be managed by admins.
  const isAdmin = roles.includes("admin");

  // Participants
  const { listExamParticipants } = useApiCall();
  const participants = useFetchedResource<ExamParticipant[], string>((exam: string) => listExamParticipants(exam));
  useRefreshEffect(() => {
    participants.reload(examId ?? "");
  }, [examId]);

  const enrichedExam: EnrichedExam | null = useMemo(() => {
    const projectsMap = projects.reduce(
      (map, project) => {
        map[project.id] = project;
        return map;
      },
      {} as { [key: string]: Project },
    );
    const companiesMap = companies.reduce(
      (map, company) => {
        map[company.id] = company;
        return map;
      },
      {} as { [key: string]: Company },
    );
    const questionSetsMap = questionSets.reduce(
      (map, questionSet) => {
        map[questionSet.id] = questionSet;
        return map;
      },
      {} as { [key: string]: QuestionSet },
    );
    const exam = exams.find((exam) => exam.id === examId);
    if (!exam) return null;
    const project = projectsMap[exam.projectId];
    const company = companiesMap[project?.companyId ?? ""];
    const questionSet = questionSetsMap[exam.questionSetId];
    return {
      ...exam,
      projectName: project?.name ?? "Onbekend project",
      project,
      companyName: company?.name ?? "Onbekend bedrijf",
      company,
      questionSetName: questionSet?.name ?? "Onbekende vragenlijst",
      questionSet,
      participants: participants.resource ?? [],
    };
  }, [exams, projects, companies, questionSets, examId, participants]);

  const canManageParticipants =
    isAdmin && enrichedExam !== null && enrichedExam.startedAt === null && enrichedExam.archivedAt === null;

  return (
    <ContentContainer size={"xl"}>
      <Loading loading={loading || !enrichedExam}>
        {enrichedExam && (
          <div>
            <div className={"flex justify-between items-center"}>
              <PageHeader>Assessment {enrichedExam.name ?? ""}</PageHeader>
              {!isCustomer && (
                <div className={"h-12 mb-6 flex items-center"}>
                  <ExamAssessor
                    exam={enrichedExam}
                    assessors={assessors}
                    currentAssessor={enrichedExam.assessorId ? enrichedExam.assessorId : "-"}
                  />
                  <ExamDetailsContextMenu exam={enrichedExam} exams={exams} projects={projects} companies={companies} />
                </div>
              )}
            </div>
            {enrichedExam.archivedAt && (
              <div
                role={"status"}
                className={
                  "mb-4 flex items-center gap-3 rounded-sm border border-amber-200 bg-amber-50 px-4 py-3 text-amber-900"
                }
              >
                <FontAwesomeIcon icon={faArchive} />
                <div>
                  <span className={"font-semibold"}>Dit examen is gearchiveerd</span>
                  <span> op {enrichedExam.archivedAt.toLocaleString("nl")}.</span>
                </div>
              </div>
            )}
            <dl className={"flex items-center"}>
              <dt
                className={
                  "rounded-l border-t border-l border-b border-brand-200 bg-brand-50 text-brand-600 px-2 font-medium"
                }
              >
                Project
              </dt>
              <dd className={"rounded-r border border-brand-200 px-2 mr-3"}>{enrichedExam.projectName}</dd>
              <dt
                className={
                  "rounded-l border-t border-l border-b border-brand-200 bg-brand-50 text-brand-600 px-2 font-medium"
                }
              >
                Bedrijf
              </dt>
              <dd className={"rounded-r border border-brand-200 px-2 mr-3"}>{enrichedExam.companyName}</dd>
              <dt
                className={
                  "rounded-l border-t border-l border-b border-brand-200 bg-brand-50 text-brand-600 px-2 font-medium"
                }
              >
                Vragenlijst
              </dt>
              <dd className={"rounded-r border border-brand-200 px-2 mr-3"}>{enrichedExam.questionSetName}</dd>
            </dl>
            <ExamResultsOverview exam={enrichedExam} isCustomer={isCustomer} />
            {!isCustomer && enrichedExam.status === "Afgerond" && (
              <ExamSendReportCard exam={enrichedExam} />
            )}
            <ExamParticipantsOverview exam={enrichedExam} canManageParticipants={canManageParticipants} />
          </div>
        )}
      </Loading>
    </ContentContainer>
  );
};

const ExamAssessor: FC<{ exam: Exam; assessors: Assessor[]; currentAssessor: string }> = (props) => {
  const { addOrUpdateExam } = useApiCall();
  const reload = useRefresh();
  const refactoredAssessors = props.assessors.map((item) => [item.id, item.first_name + " " + item.last_name]);
  const [selectedAssessor, setSelectedAssessor] = useState<string | null>(props.currentAssessor);

  const saveNewValues = useCallback(async (assessorId: string) => {
    await addOrUpdateExam(props.exam.id, {
      assessor_id: assessorId,
    });
    reload();
  }, []);

  return (
    <div>
      <Select
        label={"Assessor"}
        inline
        value={selectedAssessor ?? "-"}
        options={Object.fromEntries(refactoredAssessors)}
        onChange={async (key) => {
          setSelectedAssessor(key);
          await saveNewValues(key);
        }}
      />
    </div>
  );
};

const ExamResultsOverview: FC<{ exam: EnrichedExam; isCustomer: boolean }> = (props) => {
  const { getExamExportUrl, getExamReportUrl, exportSelectionCertificatesPdf } = useApiCall();
  const { certificates } = useApi();
  const examCertificateCount = certificates.filter((c) => c.examId === props.exam.id).length;

  const downloadCertificatesZip = async () => {
    const blob = await exportSelectionCertificatesPdf([props.exam.id], null, true);
    const file = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = file;
    link.download = `${props.exam.name ?? "certificaten"}.zip`;
    link.click();
    URL.revokeObjectURL(file);
  };
  const [override, setOverride] = useState(false);
  // props.exam.status is "Gearchiveerd" for archived exams, so derive the progress of the exam itself.
  const progress =
    props.exam.finishedAt !== null ? "Afgerond" : props.exam.startedAt === null ? "Concept" : "In uitvoering";
  return (
    <div>
      {/*<SectionDivider/>*/}
      {/*<SectionHeader>Resultaten</SectionHeader>*/}
      <Card title={"Resultaten"}>
        {(progress === "Afgerond" || override) && (
          <div className={"flex flex-col items-center space-y-3 py-4"}>
            <FontAwesomeIcon icon={faCheckCircle} className={"text-brand-900"} />
            <h2 className={"font-lg font-semibold text-brand-800"}>Finished</h2>
            {props.exam.questionSet.type.toLowerCase() === "fir" ? (
              <div className={"w-full"}>
                <div className={"grid grid-cols-2 gap-4"}>
                  <div className={""}>
                    <h3 className={"font-bold"}>Overall Test Results / Field Inspection Report [.PDF]</h3>
                    <p className={"text-slate-800"}>De uitlslagen in een .pdf bestand</p>
                  </div>
                  <div className={"flex items-center"}>
                    <Button
                      size={"md"}
                      type={"secondary"}
                      icon={faDownload}
                      text={"Download .pdf [Overall Test Results]"}
                      onClick={() => {
                        window.open(getExamReportUrl(props.exam.id), "_blank");
                        // window.open(getExamExportUrl(props.exam.id, true), '_blank')
                      }}
                    />
                  </div>
                </div>
              </div>
            ) : (
              <div className={"w-full"}>
                <div className={"grid grid-cols-2 gap-4"}>
                  {!props.isCustomer && (
                    <>
                      <div className={""}>
                        <h3 className={"font-bold"}>Overall Test Results / Competency Test [.PDF]</h3>
                        <p className={"text-slate-800"}>
                          Alle test uitslagen en scorekaarten van alle aanwezige <br />
                          contractors in één .pdf file{" "}
                        </p>
                      </div>
                      <div className={"flex items-center"}>
                        <Button
                          size={"md"}
                          type={"secondary"}
                          icon={faDownload}
                          text={"Download .pdf [Overall Test Results]"}
                          onClick={() => {
                            window.open(getExamReportUrl(props.exam.id), "_blank");
                            // window.open(getExamExportUrl(props.exam.id, true), '_blank')
                          }}
                        />
                      </div>
                    </>
                  )}

                  {!props.isCustomer && (
                    <>
                      <div className={""}>
                        <h3 className={"font-bold"}>Test Results / Competency Test per Contractor [.PDF]</h3>
                        <p className={"text-slate-800"}>
                          Alle test uitslagen en scorekaarten van alle aanwezige <br />
                          contractors apart in een .pdf file
                        </p>
                      </div>
                      <div className={"flex flex-col items-start"}>
                        {Array.from(new Set(props.exam.participants.map((p) => p.contractor) ?? [])).map(
                          (contractor, i) => {
                            return (
                              <div key={i} className={"border border-slate-200 rounded-sm w-full px-3 py-2 mb-3"}>
                                <h2 className={"font-bold text-brand-900"}>{contractor}</h2>
                                <Button
                                  size={"md"}
                                  type={"secondary"}
                                  icon={faDownload}
                                  text={`Download .pdf [Test Results per Contractor]`}
                                  onClick={() => {
                                    window.open(getExamReportUrl(props.exam.id, contractor), "_blank");
                                  }}
                                />
                              </div>
                            );
                          },
                        )}
                      </div>
                    </>
                  )}

                  <div className={""}>
                    <h3 className={"font-bold"}>Overall Test Results / Competency Test [.XSLX]</h3>
                    <p className={"text-slate-800"}>Alle test uitslagen van alle contractors in één .xlsx file</p>
                  </div>
                  <div className={"flex items-center"}>
                    <Button
                      size={"md"}
                      type={"secondary"}
                      icon={faDownload}
                      text={"Download .xlsx [Overall Test Results]"}
                      onClick={() => {
                        window.open(getExamExportUrl(props.exam.id, false), "_blank");
                      }}
                    />
                  </div>

                  {examCertificateCount > 0 && (
                    <>
                      <div className={""}>
                        <h3 className={"font-bold"}>Certificaten / Competency Test [.ZIP]</h3>
                        <p className={"text-slate-800"}>
                          De certificaten van alle deelnemers ({examCertificateCount}) van dit
                          examen in één .zip bestand
                        </p>
                      </div>
                      <div className={"flex items-center"}>
                        <Button
                          size={"md"}
                          type={"secondary"}
                          icon={faDownload}
                          text={"Download .zip [Certificaten]"}
                          onClick={downloadCertificatesZip}
                        />
                      </div>
                    </>
                  )}

                  {!props.isCustomer && (
                    <>
                      <div className={""}>
                        <h3 className={"font-bold"}>Export Overall Data Scorecard [.XSLX]</h3>
                        <p className={"text-slate-800"}>
                          Volledig export van alle ingevulde scorecards in één .xlsx file.
                        </p>
                      </div>
                      <div className={"flex flex-col items-start"}>
                        <Button
                          size={"md"}
                          type={"secondary"}
                          icon={faDownload}
                          text={`Download .xlsx [Export Overall Data Scorecard]`}
                          onClick={() => {
                            window.open(getExamExportUrl(props.exam.id, true), "_blank");
                          }}
                        />
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
        {progress === "In uitvoering" && !override && (
          <div className={"flex flex-col items-center space-y-3 py-4"}>
            <FontAwesomeIcon icon={faClock} className={"text-brand-900"} />
            <h2 className={"font-lg font-semibold text-brand-800"}>In progress</h2>
            <p className={"text-sm text-brand-700"}>
              Dit examen is nog niet afgerond, pas dan kun je de resultaten exporteren
            </p>
            <button className={"text-sm underline text-red-700"} onClick={() => setOverride(true)}>
              Toch nu exporteren
            </button>
          </div>
        )}
        {progress === "Concept" && !override && (
          <div className={"flex flex-col items-center space-y-3 py-4"}>
            <FontAwesomeIcon icon={faPencil} className={"text-brand-900"} />
            <h2 className={"font-lg font-semibold text-brand-800"}>Concept</h2>
            <p className={"text-sm text-brand-700"}>
              Dit examen is nog niet afgerond, pas dan kun je de resultaten exporteren
            </p>
            <button className={"text-sm underline text-red-700"} onClick={() => setOverride(true)}>
              Toch nu exporteren
            </button>
          </div>
        )}
        {(props.exam.photoA || props.exam.photoB || props.exam.photoC || props.exam.photoD) && (
          <ExamImagePreview exam={props.exam} />
        )}
      </Card>
    </div>
  );
};

const ExamSendReportCard: FC<{ exam: Exam }> = (props) => {
  const sendReportModal = useModal({
    title: "Rapport versturen",
    body: <SendExamReportModal exam={props.exam} />,
    size: "lg",
  });
  return (
    <Card title={"Rapport versturen"}>
      <div className={"flex items-center justify-between gap-4"}>
        <p className={"text-slate-800"}>Verstuur het rapport van dit examen per e-mail.</p>
        <Button
          type={"secondary"}
          size={"md"}
          icon={faPaperPlane}
          text={"Rapport versturen"}
          onClick={() => sendReportModal.open()}
        />
      </div>
    </Card>
  );
};

const ExamImagePreview: FC<{ exam: Exam }> = (props) => {
  return (
    <div className={"grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4"}>
      {props.exam.photoA ? (
        <div
          className={"w-full cursor-zoom-in flex items-center justify-center"}
          onClick={() => window.open(formatImageUrl(props.exam.photoA!), "_blank")}
        >
          <div
            className={"w-36 h-52 transform rounded-sm -rotate-90"}
            style={{
              backgroundImage: `url('${formatImageUrl(props.exam.photoA)}')`,
              backgroundSize: "cover",
            }}
          ></div>
        </div>
      ) : (
        <div className={"w-full cursor-zoom-in flex items-center justify-center"}>
          <div className={"w-52 h-36 rounded-sm bg-slate-100"}></div>
        </div>
      )}
      {props.exam.photoB ? (
        <div
          className={"w-full cursor-zoom-in flex items-center justify-center"}
          onClick={() => window.open(formatImageUrl(props.exam.photoB!), "_blank")}
        >
          <div
            className={"w-36 h-52 transform rounded-sm -rotate-90"}
            style={{
              backgroundImage: `url('${formatImageUrl(props.exam.photoB)}')`,
              backgroundSize: "cover",
            }}
          ></div>
        </div>
      ) : (
        <div className={"w-full cursor-zoom-in flex items-center justify-center"}>
          <div className={"w-52 h-36 rounded-sm bg-slate-100"}></div>
        </div>
      )}
      {props.exam.photoC ? (
        <div
          className={"w-full cursor-zoom-in flex items-center justify-center"}
          onClick={() => window.open(formatImageUrl(props.exam.photoC!), "_blank")}
        >
          <div
            className={"w-36 h-52 transform rounded-sm -rotate-90"}
            style={{
              backgroundImage: `url('${formatImageUrl(props.exam.photoC)}')`,
              backgroundSize: "cover",
            }}
          ></div>
        </div>
      ) : (
        <div className={"w-full cursor-zoom-in flex items-center justify-center"}>
          <div className={"w-52 h-36 rounded-sm bg-slate-100"}></div>
        </div>
      )}
      {props.exam.photoD ? (
        <div
          className={"w-full cursor-zoom-in flex items-center justify-center"}
          onClick={() => window.open(formatImageUrl(props.exam.photoD!), "_blank")}
        >
          <div
            className={"w-36 h-52 transform rounded-sm -rotate-90"}
            style={{
              backgroundImage: `url('${formatImageUrl(props.exam.photoD)}')`,
              backgroundSize: "cover",
            }}
          ></div>
        </div>
      ) : (
        <div className={"w-full cursor-zoom-in flex items-center justify-center"}>
          <div className={"w-52 h-36 rounded-sm bg-slate-100"}></div>
        </div>
      )}
    </div>
  );
};
function formatImageUrl(path: string): string {
  const url = new URL(path, config.apiBaseUrlV1);
  return url.toString();
}

const ExamParticipantsOverview: FC<{ exam: EnrichedExam; canManageParticipants: boolean }> = (props) => {
  const addParticipantModal = useModal({
    title: "Deelnemer toevoegen",
    body: <AddParticipantModal exam={props.exam} participants={props.exam.participants} />,
    size: "lg",
  });
  const scoringMap = useMemo(
    () => resolveScoringMap(props.exam, props.exam.project, props.exam.company, props.exam.questionSet),
    [props.exam],
  );
  const scoreCategories = useMemo(() => Object.keys(scoringMap), [scoringMap]);
  return (
    <div>
      <SectionDivider />
      {props.exam.questionSet.type === "Fir" ? (
        <>
          <SectionHeader>Details</SectionHeader>
          {props.exam.participants.length === 0 ? (
            <TablePlaceholder>Nog geen details</TablePlaceholder>
          ) : (
            <DataTable<ExamParticipant>
              keyProperty={"id"}
              data={props.exam.participants}
              pageSize={50}
              columns={[
                { header: "Field inspection number", sort: true, property: "name" },
                { header: "Job Number", sort: true, property: "contractor" },
                { header: "Job Location", sort: true, property: "subContractor" },
                { header: "Equipment Number", sort: true, property: "badgeNumber" },
              ]}
            />
          )}
        </>
      ) : (
        <>
          {/* SectionHeader has its own bottom margin, which is moved to the row so the heading and button align. */}
          <div className={"mb-4 flex items-center justify-between [&>h2]:mb-0"}>
            <SectionHeader>Deelnemers ({props.exam.participants.filter((p) => !p.noShow).length})</SectionHeader>
            {props.canManageParticipants && (
              <Button
                type={"primary"}
                size={"md"}
                icon={faPlus}
                text={"Deelnemer toevoegen"}
                onClick={() => addParticipantModal.open()}
              />
            )}
          </div>
          {props.exam.participants.length === 0 ? (
            <TablePlaceholder>Nog geen deelnemers</TablePlaceholder>
          ) : (
            <DataTable<ExamParticipant>
              keyProperty={"id"}
              data={props.exam.participants}
              pageSize={50}
              columns={[
                { header: "Position", property: "position" },
                {
                  header: "Naam",
                  sort: true,
                  property: "name",
                  transform: (name, participant) => (
                    <ParticipantNameCell name={name} participant={participant} />
                  ),
                },
                {
                  header: "Status",
                  property: "id",
                  transform: (_, participant) => (
                    <ParticipantStatusCell participant={participant} />
                  ),
                },
                { header: "Contractor", sort: true, property: "contractor" },
                { header: "Subcontractor", sort: true, property: "subContractor" },
                { header: "Badge", sort: true, property: "badgeNumber" },
                { header: "Certification", sort: true, property: "certification" },
                ...scoreCategories.map((category) => ({
                  header: category,
                  property: "score" as const,
                  transform: (_: string | null, participant: ExamParticipant) => {
                    const score = participant.scorePreviewData?.[category];
                    if (score === undefined) return <></>;
                    return <ScoreBadge scoringMap={scoringMap} category={category} score={score} />;
                  },
                })),
                { header: "Note", sort: true, property: "note" },
                {
                  header: "",
                  property: "id",
                  transform: (_, participant) => {
                    const p = props.exam.participants.find((p) => p.id === participant.id);
                    if (p) {
                      return (
                        <ExamDetailsTableRow
                          participant={p}
                          exam={props.exam}
                          canManage={props.canManageParticipants}
                        />
                      );
                    }
                  },
                },
              ]}
            />
          )}
        </>
      )}
    </div>
  );
};

const ParticipantNameCell: FC<{ name: string | null; participant: ExamParticipant }> = ({
  name,
  participant,
}) => {
  const archived = !!participant.deletedAt;
  const failedToAttend = participant.noShow || participant.isAborted;
  const nameColor = failedToAttend
    ? archived
      ? "text-red-400"
      : "text-red-700 font-bold"
    : archived
      ? "text-slate-400"
      : "text-slate-900";
  return (
    <div className={"flex items-center gap-1.5"}>
      <span className={`font-medium ${nameColor} ${archived ? "line-through" : ""}`}>{name}</span>
      {participant.recentAttempts !== null && participant.recentAttempts > 0 && (
        <span
          title={`Poging ${participant.recentAttempts + 1}`}
          className={
            "bg-red-700 text-white rounded-full inline-flex items-center justify-center h-4 min-w-4 px-1 text-[11px] font-bold"
          }
        >
          {participant.recentAttempts + 1}
        </span>
      )}
    </div>
  );
};

const ParticipantStatusCell: FC<{ participant: ExamParticipant }> = ({ participant }) => (
  <div className={"flex flex-wrap gap-1"}>
    {participant.noShow && <Badge style={BadgeStyle.danger}>No-show</Badge>}
    {!participant.noShow && participant.isAborted && (
      <Badge style={BadgeStyle.danger}>Abort</Badge>
    )}
    {participant.deletedAt && <Badge style={BadgeStyle.default}>Gearchiveerd</Badge>}
  </div>
);

const ExamDetailsTableRow: FC<{ participant: ExamParticipant; exam: EnrichedExam; canManage: boolean }> = (
  props,
) => {
  const { keycloak } = useKeycloak();
  const isAdmin = keycloak.tokenParsed?.realm_access?.roles?.includes("admin") ?? false;
  const archived = !!props.participant.deletedAt;
  const { certificates } = useApi();
  const { downloadCertificateUrl } = useApiCall();

  const archiveParticipantModal = useModal({
    title: archived ? "Participant terugzetten" : "Participant archiveren",
    body: <ArchiveParticipantModal participant={props.participant} />,
    size: "lg",
  });
  const renameParticipantModal = useModal({
    title: "Naam bewerken",
    body: <EditParticipantModal participant={props.participant} />,
    size: "md",
  });
  const editParticipantModal = useModal({
    title: "Deelnemer bewerken",
    body: (
      <EditParticipantModal
        participant={props.participant}
      />
    ),
    size: "lg",
  });
  const permanentDeleteParticipantModal = useModal({
    title: "Participant permanent verwijderen",
    body: <PermanentDeleteParticipantModal participant={props.participant} />,
    size: "lg",
  });
  const certificate = certificates.find(
    (c) => c.examId === props.participant.examId && c.participantId === props.participant.id,
  );

  const downloadCertificate = async () => {
    if (!certificate) return;
    const link = document.createElement("a");
    link.href = await downloadCertificateUrl(certificate.id);
    link.download = certificate.downloadFileName;
    link.target = "_blank";
    link.click();
  };

  return (
    <div className={"flex justify-end"}>
      <ContextMenu
        options={[
          ...(props.canManage && !archived
            ? [
                {
                  icon: faPencil,
                  label: "Deelnemer bewerken",
                  onClick: () => editParticipantModal.open(),
                },
                {
                  icon: faTrash,
                  label: "Deelnemer verwijderen",
                  danger: true,
                  onClick: () => permanentDeleteParticipantModal.open(),
                },
              ]
            : [
                {
                  icon: faPencil,
                  label: "Naam bewerken",
                  onClick: () => renameParticipantModal.open(),
                },
                {
                  icon: archived ? faArrowRotateLeft : faArchive,
                  label: archived ? "Participant terugzetten" : "Participant archiveren",
                  danger: !archived,
                  onClick: () => archiveParticipantModal.open(),
                },
              ]),
          ...(archived && isAdmin
            ? [
                {
                  icon: faTrash,
                  label: "Permanent verwijderen",
                  danger: true,
                  onClick: () => permanentDeleteParticipantModal.open(),
                },
              ]
            : []),
        ]}
      />
    </div>
  );
};

const ExamDetailsContextMenu: FC<{
  exam: EnrichedExam;
  companies: Company[];
  projects: Project[];
  exams: Exam[];
}> = (props) => {
  const [expanded, setExpanded] = useState<boolean>(false);
  const { keycloak } = useKeycloak();
  const roles = useMemo(() => keycloak.tokenParsed?.realm_access?.roles ?? [], [keycloak.tokenParsed]);
  const revertExamModal = useModal({
    title: "Examen terugzetten",
    body: <RevertExamModal exam={props.exam} />,
  });
  const archiveExamModal = useModal({
    title: "Examen archiveren",
    body: <ArchiveExamModal exam={props.exam} />,
  });
  const permanentDeleteExamModal = useModal({
    title: "Examen permanent verwijderen",
    body: <PermanentDeleteExamModal exam={props.exam} />,
  });
  const editExamModal = useModal({
    title: "Examen bewerken",
    size: "xl",
    body: <EditExamModal companies={props.companies} projects={props.projects} exams={props.exams} exam={props.exam} />,
  });
  const copyExamModal = useModal({
    title: "Examen dupliceren",
    body: (
      <CloneExamModal companies={props.companies} projects={props.projects} exams={props.exams} exam={props.exam} />
    ),
  });
  const moveParticipantModal = useModal({
    title: "Deelnemer verplaatsen",
    body: <MoveExamParticipantModal exams={props.exams} exam={props.exam} />,
  });
  const regenResultsModal = useModal({
    title: "Certificaten opnieuw maken/versturen",
    body: <RegenResultsModal exam={props.exam} />,
  });

  const options = (
    <>
      {props.exam.archivedAt ? (
        <>
          <ExamDetailsContextMenuOption text={"Examen terugzetten"} icon={faBackward} onClick={() => revertExamModal.open()} />
          {roles.includes("admin") && (
            <ExamDetailsContextMenuOption
              danger
              text={"Examen permanent Verwijderen"}
              icon={faCircleExclamation}
              onClick={() => permanentDeleteExamModal.open()}
            />
          )}
        </>
      ) : (
        <>
          {roles.includes("admin") && (
            <ExamDetailsContextMenuOption
              text={"Examen bewerken"}
              icon={faPencil}
              onClick={() => editExamModal.open()}
            />
          )}
          {roles.includes("admin") && (
            <ExamDetailsContextMenuOption
              text={"Examen dupliceren"}
              icon={faCopy}
              onClick={() => copyExamModal.open()}
            />
          )}
          {roles.includes("admin") && (
            <ExamDetailsContextMenuOption
              text={"Deelnemer verplaatsen"}
              icon={faArrowsLeftRight}
              onClick={() => moveParticipantModal.open()}
            />
          )}
          {roles.includes("admin") && (
            <ExamDetailsContextMenuOption
              text={"Opnieuw verwerken"}
              icon={faRefresh}
              onClick={() => regenResultsModal.open()}
            />
          )}
          <ExamDetailsContextMenuOption text={"Examen archiveren"} danger icon={faArchive} onClick={() => archiveExamModal.open()} />
        </>
      )}
    </>
  );

  return (
    <div className={"ml-3 mt-1 relative"}>
      <IconButton size={"md"} type={"primary"} icon={faEllipsisV} onClick={() => setExpanded(true)} />
      {expanded && (
        <>
          <div className={"fixed inset-0 bg-[#0001]"} onClick={() => setExpanded(false)}></div>
          <div className={"absolute top-10 right-0 min-w-48 bg-white rounded-sm shadow-lg border border-slate-300"}>
            {options}
          </div>
        </>
      )}
    </div>
  );
};
const ExamDetailsContextMenuOption: FC<{
  text: string;
  icon: IconDefinition;
  danger?: true;
  onClick: () => Promise<void> | void;
}> = (props) => {
  return (
    <button
      className={`flex w-full items-center h-10 px-2 ${props.danger ? "text-red-600 hover:bg-red-50" : "text-brand-900 hover:bg-brand-50"}`}
      onClick={props.onClick}
    >
      <div className={"w-10 h-10 flex items-center justify-center -ml-2"}>
        <FontAwesomeIcon icon={props.icon} className={""} />
      </div>
      <span className={"whitespace-nowrap"}>{props.text}</span>
    </button>
  );
};
